import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Users, Target, TrendingUp, Crown, ChevronDown, ChevronRight, Briefcase, DollarSign } from 'lucide-react';

import { getTeamsUnderManager } from '../utils/teamHierarchy';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmtINR = (v) => { if (!v && v !== 0) return '—'; return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v); };
const fmtUSD = (v) => { if (!v && v !== 0) return '—'; return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(v); };

const ManagerDetailPage = () => {
  const { managerId } = useParams();
  const navigate = useNavigate();
  const { isSuperadmin, isCeo } = useAuth();
  const canSeeAmounts = isSuperadmin || isCeo;

  const [manager, setManager] = useState(null);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTeams, setExpandedTeams] = useState({});

  // Date range defaults: 1st of current month → today
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);

  useEffect(() => { fetchData(); }, [managerId, dateFrom, dateTo]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [analyticsRes, monthlyRes, userRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/team-analytics?date_from=${dateFrom}&date_to=${dateTo}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/team-monthly-data?date_from=${dateFrom}&date_to=${dateTo}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/user-directory`, { withCredentials: true }),
      ]);

      const mgrUser = userRes.data.find(u => u.id === managerId);
      setManager(mgrUser || null);

      // Filter teams belonging to this manager
      const mgrTeams = analyticsRes.data.filter(t => t.manager_id === managerId && !t.name?.toLowerCase().includes('direct'));
      const monthlyTeams = monthlyRes.data?.teams || [];

      // Merge monthly data into analytics teams, preserving analytics members
      const merged = mgrTeams.map(t => {
        const mt = monthlyTeams.find(x => x.id === t.id);
        return { ...t, ...(mt || {}), members: t.members, analytics_data: t };
      });

      const mgrGroup = {
        manager_id: managerId,
        manager_name: mgrUser?.name || '',
        teams: merged,
      };
      setTeams(getTeamsUnderManager(mgrGroup));
    } catch (e) { console.error('Failed to fetch manager data:', e); }
    finally { setLoading(false); }
  };

  const toggleExpand = (teamId) => setExpandedTeams(prev => ({ ...prev, [teamId]: !prev[teamId] }));

  // Summary stats
  const totalMembers = teams.reduce((s, t) => s + (t.target_headcount || t.member_count || 0), 0);
  const totalCompanyTarget = teams.reduce((s, t) => s + (t.company_target || 0), 0) * 3;
  const totalReached = teams.reduce((s, t) => s + (t.reached_target || 0), 0);
  const totalTeamTarget = teams.reduce((s, t) => s + (t.team_given_target || 0), 0) * 3;
  const totalPlacements = teams.reduce((s, t) => s + (t.total_placements || 0), 0);
  const totalBackouts = teams.reduce((s, t) => s + (t.total_backouts || 0), 0);
  const totalIncentive = teams.reduce((s, t) => s + (t.total_incentive || 0), 0);
  const totalMargin = teams.reduce((s, t) => s + (t.total_saved_margin || 0), 0);
  const totalSpending = teams.reduce((s, t) => s + (t.team_spending || 0), 0);

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
    </div>
  );

  return (
    <div className="space-y-6 max-w-[1400px] mx-auto" data-testid="manager-detail-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft size={18} />
          </Button>
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg">
            <Crown size={24} className="text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900" data-testid="manager-name">{manager?.name || 'Manager'}</h1>
            <p className="text-sm text-slate-500">{manager?.role?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l.toUpperCase())} — {manager?.organization || ''} — {teams.length} Team{teams.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Label className="text-xs text-slate-500">From</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-[150px] h-8 text-xs" data-testid="mgr-date-from" />
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-slate-500">To</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-[150px] h-8 text-xs" data-testid="mgr-date-to" />
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {[
          { label: 'Total Members', value: totalMembers, icon: <Users size={18} />, color: 'text-blue-700', bg: 'bg-blue-50 border-blue-200' },
          { label: 'Company Target', value: `${totalReached}/${totalCompanyTarget}`, icon: <Target size={18} />, color: totalReached >= totalCompanyTarget ? 'text-emerald-700' : 'text-red-700', bg: totalReached >= totalCompanyTarget ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200' },
          { label: 'Team Target', value: totalTeamTarget > 0 ? `${totalReached}/${totalTeamTarget}` : '—', icon: <Target size={18} />, color: 'text-violet-700', bg: 'bg-violet-50 border-violet-200' },
          { label: 'Placements', value: totalPlacements, icon: <Briefcase size={18} />, color: 'text-slate-700', bg: 'bg-slate-50 border-slate-200' },
          { label: 'Backouts', value: totalBackouts, icon: <Briefcase size={18} />, color: 'text-red-700', bg: 'bg-red-50 border-red-200' },
          ...(canSeeAmounts ? [
            { label: 'Spending', value: fmtINR(totalSpending), icon: <TrendingUp size={18} />, color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200' },
            { label: 'Incentive', value: fmtINR(totalIncentive), icon: <DollarSign size={18} />, color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200' },
            { label: 'Margin', value: fmtUSD(totalMargin), icon: <TrendingUp size={18} />, color: 'text-teal-700', bg: 'bg-teal-50 border-teal-200' },
          ] : []),
        ].map(card => (
          <Card key={card.label} className={`${card.bg} border`}>
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-1">
                <span className={card.color}>{card.icon}</span>
                <span className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">{card.label}</span>
              </div>
              <p className={`text-lg font-bold ${card.color}`} data-testid={`summary-${card.label.toLowerCase().replace(/\s/g, '-')}`}>{card.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Teams */}
      <div className="space-y-4">
        {teams.length === 0 ? (
          <Card><CardContent className="p-8 text-center text-slate-500">No teams found for this manager.</CardContent></Card>
        ) : teams.map(team => {
          const isExpanded = expandedTeams[team.id];
          const targetPct = team.company_target > 0 ? Math.round((team.reached_target / (team.company_target * 3)) * 100) : 0;
          const members = team.members || [];
          const leadName = team.lead_name || '—';
          const asstMgr = team.assistant_manager_name;

          return (
            <Card key={team.id} className="border-slate-200 overflow-hidden" data-testid={`team-card-${team.id}`}>
              <div className="p-4 cursor-pointer hover:bg-slate-50 transition-colors" onClick={() => toggleExpand(team.id)}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {isExpanded ? <ChevronDown size={18} className="text-slate-400" /> : <ChevronRight size={18} className="text-slate-400" />}
                    <div>
                      <p className="font-semibold text-slate-900">{team.name}</p>
                      <p className="text-xs text-slate-500">
                        {team.department?.toUpperCase()} — Lead: <strong>{leadName}</strong>
                        {asstMgr && <> — Asst. Mgr: <strong>{asstMgr}</strong></>}
                        {' — '}{team.target_headcount || team.member_count || 0} members
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6 text-xs">
                    <div className="text-center">
                      <p className="text-[9px] text-slate-400 uppercase">Company Target</p>
                      <p className={`font-bold ${targetPct >= 100 ? 'text-emerald-700' : 'text-slate-700'}`}>{team.reached_target}/{team.company_target * 3}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-[9px] text-slate-400 uppercase">Team Target</p>
                      <p className="font-bold text-slate-700">{team.team_given_target > 0 ? `${team.reached_target}/${team.team_given_target * 3}` : '—'}</p>
                    </div>
                    {canSeeAmounts && (
                      <>
                        <div className="text-center">
                          <p className="text-[9px] text-slate-400 uppercase">Incentives</p>
                          <p className="font-bold text-emerald-700">{fmtINR(team.total_incentive || 0)}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-[9px] text-slate-400 uppercase">Margin</p>
                          <p className="font-bold text-teal-700">{fmtUSD(team.total_saved_margin || 0)}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-[9px] text-slate-400 uppercase">Backouts</p>
                          <p className="font-bold text-red-600">{team.total_backouts || 0}</p>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                {/* Target Progress */}
                <div className="mt-3 flex items-center gap-3">
                  <div className="flex-1 bg-slate-200/60 rounded-full h-2.5 overflow-hidden">
                    <div className={`h-full rounded-full transition-all duration-500 ${targetPct >= 100 ? 'bg-emerald-500' : targetPct >= 50 ? 'bg-amber-500' : 'bg-red-400'}`} style={{ width: `${Math.min(targetPct, 100)}%` }} />
                  </div>
                  <span className={`text-xs font-semibold ${targetPct >= 100 ? 'text-emerald-700' : 'text-slate-600'}`}>{targetPct}%</span>
                </div>
              </div>

              {/* Expanded: Member Table */}
              {isExpanded && (
                <div className="border-t border-slate-100">
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-slate-50 text-slate-500 uppercase tracking-wider">
                          <th className="text-left p-3 font-medium">Name</th>
                          <th className="text-left p-3 font-medium">Role</th>
                          <th className="text-center p-3 font-medium">Placements</th>
                          <th className="text-center p-3 font-medium">Approved</th>
                          <th className="text-center p-3 font-medium">Effective</th>
                          <th className="text-center p-3 font-medium">Backouts</th>
                          {canSeeAmounts && (
                            <>
                              <th className="text-right p-3 font-medium">Salary</th>
                              <th className="text-right p-3 font-medium">Margin</th>
                              <th className="text-right p-3 font-medium">Incentive</th>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {members.map((m, idx) => (
                          <tr key={m.id || idx} className="border-t border-slate-100 hover:bg-blue-50/50 transition-colors">
                            <td className="p-3">
                              <span className="font-medium text-blue-700 cursor-pointer hover:underline"
                                    onClick={() => navigate(`/dashboard/user/${m.id}`)}
                                    data-testid={`member-name-${m.id}`}
                              >{m.name}</span>
                            </td>
                            <td className="p-3 capitalize text-slate-500">{m.role?.replace(/_/g, ' ')}</td>
                            <td className="p-3 text-center font-medium">{m.placements || 0}</td>
                            <td className="p-3 text-center font-semibold text-emerald-700">{m.approved || 0}{(m.low_end_approved || 0) > 0 && <span className="text-[9px] text-amber-600 ml-0.5">({m.low_end_approved} LE)</span>}</td>
                            <td className="p-3 text-center font-semibold text-blue-700">{m.effective_approved || 0}</td>
                            <td className="p-3 text-center font-bold text-red-600">{m.backouts || 0}</td>
                            {canSeeAmounts && (
                              <>
                                <td className="p-3 text-right text-slate-600">{fmtINR(m.salary || 0)}</td>
                                <td className="p-3 text-right font-medium text-teal-700">{m.saved_margin > 0 ? fmtUSD(m.saved_margin) : '—'}</td>
                                <td className="p-3 text-right font-semibold text-emerald-700">{fmtINR(m.incentive || 0)}<br/><span className="text-[9px] text-slate-400">{fmtUSD(Math.round((m.incentive || 0) / 83.5))}</span></td>
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default ManagerDetailPage;
